import { EntityConfig } from './types';
import { Badge } from '@/components/ui/badge';
import { Briefcase } from 'lucide-react';
import { format } from 'date-fns';

export interface Job {
  id: string;
  title: string;
  description: string | null;
  requirements: string | null;
  client_id: string | null;
  location: string | null;
  city: string | null;
  country: string | null;
  work_mode: string | null;
  job_type: string | null;
  status: string | null;
  salary_min: number | null;
  salary_max: number | null;
  salary_currency: string | null;
  hide_salary: boolean | null;
  hide_client: boolean | null;
  assigned_to: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string | null;
  published_at: string | null;
}

const STATUS_OPTIONS = [
  { label: 'Draft', value: 'draft' },
  { label: 'Published', value: 'published' },
  { label: 'Closed', value: 'closed' },
  { label: 'Archived', value: 'archived' },
];

const JOB_TYPE_OPTIONS = [
  { label: 'Full Time', value: 'full_time' },
  { label: 'Part Time', value: 'part_time' },
  { label: 'Contract', value: 'contract' },
  { label: 'Freelance', value: 'freelance' },
  { label: 'Internship', value: 'internship' },
];

const WORK_MODE_OPTIONS = [
  { label: 'On-site', value: 'onsite' },
  { label: 'Remote', value: 'remote' },
  { label: 'Hybrid', value: 'hybrid' },
];

const getStatusVariant = (status: string | null) => {
  switch (status) {
    case 'published': return 'default';
    case 'draft': return 'secondary';
    case 'closed': return 'outline';
    case 'archived': return 'destructive';
    default: return 'secondary';
  }
};

export const jobsConfig: EntityConfig<Job> = {
  name: 'jobs',
  singularName: 'Job',
  pluralName: 'Jobs',
  tableName: 'jobs',
  keyField: 'id',
  icon: <Briefcase className="h-5 w-5" />,
  description: 'Manage job openings and positions',
  
  permissionModule: 'jobs',
  
  defaultSelect: '*, clients(name)',
  defaultOrderBy: { field: 'created_at', ascending: false },
  searchFields: ['title', 'location', 'description'],
  defaultPageSize: 25,
  
  columns: [
    {
      id: 'title',
      header: 'Title',
      accessorKey: 'title',
      sortable: true,
    },
    {
      id: 'client',
      header: 'Client',
      accessorFn: (row: any) => row.clients?.name || '-',
      sortable: false,
    },
    {
      id: 'location',
      header: 'Location',
      accessorKey: 'location',
      cell: (row) => row.location || '-',
      sortable: true,
    },
    {
      id: 'job_type',
      header: 'Type',
      accessorKey: 'job_type',
      cell: (row) => (
        <Badge variant="outline" className="capitalize">
          {row.job_type?.replace('_', ' ') || '-'}
        </Badge>
      ),
      sortable: true,
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      cell: (row) => (
        <Badge variant={getStatusVariant(row.status)} className="capitalize">
          {row.status || 'Draft'}
        </Badge>
      ),
      sortable: true,
    },
    {
      id: 'created_at',
      header: 'Created',
      accessorKey: 'created_at',
      cell: (row) => format(new Date(row.created_at), 'MMM d, yyyy'),
      sortable: true,
      defaultVisible: false,
    },
  ],
  
  formFields: [
    {
      name: 'title',
      label: 'Job Title',
      type: 'text',
      required: true,
      placeholder: 'e.g. Senior Software Engineer',
      gridSpan: 2,
    },
    {
      name: 'client_id',
      label: 'Client',
      type: 'relation',
      relation: {
        table: 'clients',
        labelField: 'name',
        valueField: 'id',
        filter: { is_active: true },
      },
      gridSpan: 1,
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      options: STATUS_OPTIONS,
      defaultValue: 'draft',
      gridSpan: 1,
    },
    {
      name: 'job_type',
      label: 'Job Type',
      type: 'select',
      options: JOB_TYPE_OPTIONS,
      gridSpan: 1,
    },
    {
      name: 'work_mode',
      label: 'Work Mode',
      type: 'select',
      options: WORK_MODE_OPTIONS,
      gridSpan: 1,
    },
    {
      name: 'location',
      label: 'Location',
      type: 'text',
      placeholder: 'e.g. New York, NY',
      gridSpan: 1,
    },
    {
      name: 'salary_currency',
      label: 'Currency',
      type: 'select',
      options: [
        { label: 'USD', value: 'USD' },
        { label: 'EUR', value: 'EUR' },
        { label: 'GBP', value: 'GBP' },
        { label: 'INR', value: 'INR' },
      ],
      defaultValue: 'USD',
      gridSpan: 1,
    },
    {
      name: 'salary_min',
      label: 'Min Salary',
      type: 'number',
      placeholder: '50000',
      gridSpan: 1,
    },
    {
      name: 'salary_max',
      label: 'Max Salary',
      type: 'number',
      placeholder: '100000',
      gridSpan: 1,
    },
    {
      name: 'hide_salary',
      label: 'Hide Salary',
      type: 'switch',
      defaultValue: false,
      helpText: 'Hide salary range from candidates',
      gridSpan: 1,
    },
    {
      name: 'hide_client',
      label: 'Hide Client',
      type: 'switch',
      defaultValue: false,
      helpText: 'Hide client name from candidates',
      gridSpan: 1,
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      placeholder: 'Job description...',
      gridSpan: 2,
    },
    {
      name: 'requirements',
      label: 'Requirements',
      type: 'textarea',
      placeholder: 'Job requirements...',
      gridSpan: 2,
    },
  ],
  
  filters: [
    {
      id: 'status',
      label: 'Status',
      field: 'status',
      type: 'select',
      options: [
        { label: 'All Status', value: 'all' },
        ...STATUS_OPTIONS,
      ],
      defaultValue: 'all',
    },
    {
      id: 'job_type',
      label: 'Job Type',
      field: 'job_type',
      type: 'select',
      options: [
        { label: 'All Types', value: 'all' },
        ...JOB_TYPE_OPTIONS,
      ],
      defaultValue: 'all',
    },
  ],
  
  audit: {
    enabled: true,
    excludeFields: ['updated_at'],
    detailedChangelog: true,
  },
  
  notifications: {
    onCreate: {
      eventType: 'job_created',
      recipients: 'admins',
    },
    onUpdate: {
      eventType: 'job_updated',
      recipients: 'assigned',
    },
  },
  
  routes: {
    list: '/recruiter/jobs',
    detail: '/recruiter/jobs/:id/kanban',
  },
  
  features: {
    canCreate: true,
    canUpdate: true,
    canDelete: true,
    canBulkDelete: true,
    canExport: true,
    canSearch: true,
    showActivityLog: true,
  },
};
